<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import BcsDynamicText from '../BcsDynamicText.vue';
import { RequestService } from '../../services/RequestService'
import { EmailUtilsService } from '../../services/EmailUtilsService'
import { handleErrors } from '../../utils/errorHandler'
import AlertNotification from '../AlertNotification.vue';
import emailSendedImage from '@/assets/images/email-sended.svg'
import { FirebaseStorageService } from '../../services/FirebaseStorageService'
const { getImageUrl } = FirebaseStorageService()

const { post } = RequestService()
const { getEmailTemplate, getEmailRecipient } = EmailUtilsService()

const formData = ref({
    fullName: '',
    email: '',
    message: '',
})

const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,12}$/
const emailSended = ref(false)
const emailSendedError = ref(false)
const loading = ref(false)
const functionsBaseUrl = inject('functionsBaseUrl')
const imageUrl = ref('')


onMounted(async () => {
    imageUrl.value = await getImageUrl('sales-page-compliance-solutions/requestDemo', 'request-demo-background.png')
})

const isValidEmail = computed(() => {
    return emailRegex.test(formData.value.email)
})

const disabledButtons = computed(() => {
    return !formData.value.fullName ||
        !formData.value.email ||
        !isValidEmail.value ||
        !formData.value.message ||
        formData.value.fullName.trim().length < 1 ||
        formData.value.message.trim().length < 1
})

const resetForm = () => {
    emailSendedError.value = false
    formData.value = {
        fullName: '',
        email: '',
        message: '',
    }
}


const handleSubmit = async () => {
    try {
        loading.value = true
        formData.value.fullName = formData.value.fullName.trim()
        formData.value.message = formData.value.message.trim()
        await post('sendEmail', `${functionsBaseUrl}/utilityGateway/api/v1/email/send`, {
            type: 'REQUEST_DEMO',
            email: formData.value.email,
            data: {
                fullName: formData.value.fullName,
                message: formData.value.message,
                location: 'www.benefitscompliancesolutions.com'
            },
            emailTemplate: await getEmailTemplate('REQUEST_DEMO_EMAIL'),
            emailRecipient: await getEmailRecipient('REQUEST_DEMO_EMAIL')
        })
        formData.value = {
            fullName: '',
            email: '',
            message: ''
        }
        emailSended.value = true
    } catch (error) {
        handleErrors('Error sending email:', error)
        emailSendedError.value = true
        emailSended.value = false
    } finally {
        loading.value = false
    }
}


</script>

<template>
    <div class="request-demo-container d-flex justify-content-center" :style="{ backgroundImage: `url(${imageUrl})` }">
        <div class="overlay"></div>
        <div class="form">
            <h3>Request a Demo</h3>
            <div class="subtitle">
                <BcsDynamicText :isLandingPage="true" text="requestDemoIntro" />
            </div>

            <div v-if="!emailSended && !emailSendedError" class="input-container">
                <div class="mb-3 pt-2 input-group d-flex flex-column">
                    <div class="input-wrapper">
                        <input placeholder="Full Name" type="text" class="form-control text-16 form-input"
                            v-model="formData.fullName">
                    </div>
                </div>
                <div class="mb-3 pt-2 input-group d-flex flex-column">

                    <div class="input-wrapper">
                        <input placeholder="Email" type="text" class="form-control text-16 form-input"
                            v-model="formData.email">
                    </div>
                </div>
                <div style="padding-bottom: 30px;" class="pt-2 text-area-container d-flex flex-column">

                    <textarea class="form-control" rows="5" v-model="formData.message"
                        placeholder="e.g., I would like to book a demo of your software."></textarea>
                </div>

                <div class="actions-container d-flex w-100">

                    <button @click="handleSubmit" type="button" :disabled="disabledButtons"
                        class="btn btn-secondary btn-submit btn-primary d-flex justify-content-center align-items-center"
                        style="width: 382px;">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <span v-else>REQUEST A DEMO</span>
                    </button>

                    <button @click="resetForm" type="button" style="width: 212px;"
                        class="btn d-flex align-items-center justify-content-center btn-reset btn-light ">
                        <i class="bi bi-x-circle rounded-circle d-flex justify-content-center align-items-center"></i>
                        &nbsp; CLEAR FORM
                    </button>
                </div>

            </div>

            <div v-else class="email-success">

                <img v-if="emailSended" class="email-sended-img" :src="emailSendedImage"
                    alt="Email sent successfully" />

                <div class="email-sended-content">
                    <BcsDynamicText :isLandingPage="true" v-if="emailSended" text="emailSendedSucessCopy" />
                    <div class="send-another-message-container">
                        <button @click="emailSended = false, emailSendedError = false" class="btn btn-primary">Send
                            Another Message</button>
                    </div>
                </div>
            </div>

            <div v-if="emailSendedError" style="padding-bottom: 50px;">
                <AlertNotification class="email-sended-error fw-bold" :typeAlert="'error'" :showCloseButton="false">
                    <BcsDynamicText :isLandingPage="true" text="emailSendedErrorCopy" />
                </AlertNotification>
            </div>

        </div>
    </div>
</template>
<style scoped lang="scss">
.request-demo-container {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    color: var(--bs-white);

    .form-control {
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-ou
    }

    .btn-secondary {
        background-color: transparent !important;
        border: 1px solid #fff !important;
        transition: all 0.4s ease;

        &:hover {
            border: none !important;
            background-color: #161634 !important;
            color: var(--bs-white) !important;
        }
    }

    .btn-primary {
        transition: all 0.4s ease;
    }

    .overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(46, 45, 102, 0.8);
        z-index: 0;
    }

    .form {
        position: relative;
        z-index: 1;
        width: 40%;
    }

    .btn-reset {
        background-color: var(--bs-white) !important;
        transition: all 0.4s ease;

        &:hover {
            border: none !important;
            background-color: #161634 !important;
            color: var(--bs-white) !important;
        }

    }




    .subtitle {
        font-size: 16px;
    }

    .input-wrapper {
        width: 100%;
    }

    .form {
        h3 {
            padding-top: 85px;
            padding-bottom: 12px;
        }

        input {
            height: 50px;
            border-radius: 0 !important;
            border: 1px solid #000;
            color: #8C8C8C !important;
            font-size: 18px !important;
            font-style: italic !important;
            transition: all 0.4s ease;

            &:focus {
                border-color: none !important;
                box-shadow: none !important;
            }


        }

        .input-container {
            margin-top: 11px;
        }

        .input-group {
            span {
                margin-bottom: 9px;
            }
        }

        textarea {
            resize: none;
            border: 1px solid #000 !important;
            margin-top: 9px !important;
            border-radius: 0 !important;
            color: #8C8C8C !important;
            font-size: 18px !important;
            font-style: italic !important;
            transition: all 0.4s ease;

            &:focus {
                border-color: none !important;
                box-shadow: none !important;
            }
        }

        .text-area-container {
            width: 100%;
        }

        .actions-container {
            gap: 12px;
            padding-bottom: 83px;

            .btn-submit,
            .btn-reset {
                height: 45px;
                font-size: 16px !important;
                transition: all 0.4s ease;
            }
        }
    }
}

.email-success {
    padding-bottom: 30px;
    width: 71%;
    text-align: center;
    margin-top: 40px;
    color: var(--bs-white);

    .send-another-message-container {
        margin-top: 20px;

        .btn-primary {
            background-color: transparent !important;
            border-color: var(--bs-white) !important;
            transition: all 0.4s ease;

            &:hover {
                background-color: #161634 !important;
                border-color: transparent !important;
            }
        }
    }
}

@media (max-width: 1030px) {

    .email-success {
        width: 100%;
    }

    .request-demo-container .form h3 {
        padding-top: 43px;
        text-align: center;
        font-size: 26px;
    }

    .request-demo-container .form .actions-container {
        flex-direction: column;
        padding: 0 20px;
        padding-bottom: 50px;

        .btn-submit,
        .btn-reset {
            width: 100% !important;
            justify-content: center;
            font-size: 20px !important;
        }
    }

    .request-demo-container {
        width: 100%;
        height: auto;

        .image {
            display: none;
        }

        .subtitle {
            font-size: 18px;
            width: 100%;
            padding: 0 20px;
        }

        .form {
            width: 100%;

            .input-group {
                width: 100%;
                padding: 0 20px;
            }

            .text-area-container {
                width: 100%;
                padding: 0 20px;
            }

            .input-wrapper {
                width: 100%;
            }

            .form-input {
                height: 50px;
            }
        }
    }
}
</style>